import React, { useEffect, useMemo, useState } from 'react';

type ToastType = 'default' | 'success' | 'error' | 'info' | 'loading';

type ToastRecord = {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
};

type ToastOptions = {
  id?: string | number;
  duration?: number;
  position?: 'top-center' | 'bottom-center';
  className?: string;
  style?: React.CSSProperties;
};

type ToastListener = (toasts: ToastRecord[]) => void;

const listeners = new Set<ToastListener>();
let toastStore: ToastRecord[] = [];

const emit = () => {
  listeners.forEach((listener) => listener(toastStore));
};

const upsertToast = (type: ToastType, message: string, options?: ToastOptions): string => {
  const id = String(options?.id ?? `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const duration = options?.duration ?? (type === 'loading' ? Infinity : 4000);

  const index = toastStore.findIndex((toast) => toast.id === id);
  const nextToast: ToastRecord = {
    id,
    message,
    type,
    duration,
    className: options?.className,
    style: options?.style,
  };

  if (index >= 0) {
    toastStore[index] = nextToast;
  } else {
    toastStore = [...toastStore, nextToast];
  }

  emit();
  return id;
};

export const toast = {
  loading: (message: string, options?: ToastOptions) => upsertToast('loading', message, options),
  success: (message: string, options?: ToastOptions) => upsertToast('success', message, options),
  error: (message: string, options?: ToastOptions) => upsertToast('error', message, options),
  info: (message: string, options?: ToastOptions) => upsertToast('info', message, options),
  dismiss: (id?: string | number) => {
    if (id == null) {
      toastStore = [];
    } else {
      toastStore = toastStore.filter((toastItem) => toastItem.id !== String(id));
    }
    emit();
  },
};

const baseClasses = 'rounded-md px-3 py-2 text-xs text-white shadow-md backdrop-blur-sm';

const toastTypeClasses: Record<ToastType, string> = {
  default: 'bg-neutral-700/95',
  success: 'bg-emerald-700/95',
  error: 'bg-red-700/95',
  info: 'bg-blue-700/95',
  loading: 'bg-blue-700/95',
};

type ToasterProps = {
  position?: 'top-center' | 'bottom-center';
  richColors?: boolean;
};

export const Toaster: React.FC<ToasterProps> = ({ position = 'top-center' }) => {
  const [toasts, setToasts] = useState<ToastRecord[]>(toastStore);

  useEffect(() => {
    const listener: ToastListener = (nextToasts) => {
      setToasts([...nextToasts]);
    };

    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    const timers = toasts
      .filter((toastItem) => Number.isFinite(toastItem.duration))
      .map((toastItem) => window.setTimeout(() => toast.dismiss(toastItem.id), toastItem.duration));

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [toasts]);

  const renderedToasts = useMemo(() => [...toasts], [toasts]);
  const positionClass =
    position === 'bottom-center'
      ? 'bottom-8 top-auto'
      : 'top-24';

  return (
    <div className={`pointer-events-none fixed left-1/2 z-[100] flex -translate-x-1/2 flex-col gap-2 ${positionClass}`}>
      {renderedToasts.map((toastItem) => (
        <div
          key={toastItem.id}
          className={`${baseClasses} ${toastTypeClasses[toastItem.type]} ${toastItem.className ?? ''}`}
          style={toastItem.style}
        >
          {toastItem.message}
        </div>
      ))}
    </div>
  );
};
